import React, { useState } from 'react';
import { Badge, IconButton, Popover, List, ListItem, ListItemText, Typography, Button, Box, Divider } from '@mui/material';
import { Notifications, Clear, ClearAll, PlayArrow } from '@mui/icons-material';
import useNotifications from '@/hooks/common/useNotifications';
import { getNotificationIcon, getNotificationColor } from '@/components/ui/styles/notificationUtils';

interface NotificationBellProps {
  userEmail: string | null;
  isAdmin: boolean;
  onVideoClick?: (videoId: string) => void;
}

const NotificationBell: React.FC<NotificationBellProps> = ({
  userEmail,
  isAdmin,
  onVideoClick
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const {
    notifications,
    unreadCount,
    markAllAsRead,
    clearNotification,
    clearAllNotifications,
  } = useNotifications(userEmail, isAdmin);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    // Mark all notifications as read when opening
    if (unreadCount > 0) {
      markAllAsRead();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleVideoClick = (videoId: string) => {
    if (onVideoClick) {
      onVideoClick(videoId);
    }
    handleClose();
  };

  const handleClearNotification = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    clearNotification(notificationId);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'notification-popover' : undefined;

  return (
    <>
      <IconButton
        aria-describedby={id}
        onClick={handleClick}
        color="inherit"
        size="large"
      >
        <Badge badgeContent={unreadCount} color="error">
          <Notifications />
        </Badge>
      </IconButton>
      
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 400,
              maxHeight: 500,
              minWidth: 300,
            }
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Notifications {unreadCount > 0 && `(${unreadCount})`}
          </Typography>
          
          {notifications.length > 0 && (
            <Button
              startIcon={<ClearAll />}
              onClick={() => clearAllNotifications()}
              size="small"
              variant="outlined"
              sx={{ mb: 1 }}
            >
              Clear All
            </Button>
          )}
        </Box>
        
        <Divider />
        
        {notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              No notifications
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 350, overflow: 'auto' }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  component="div"
                  onClick={() => {
                    if (notification.video_id) {
                      handleVideoClick(notification.video_id);
                    }
                  }}
                  sx={{
                    cursor: notification.video_id ? 'pointer' : 'default',
                    bgcolor: notification.status === 'unread' ? 'action.hover' : 'transparent',
                    borderLeft: `4px solid ${getNotificationColor(notification.event)}`,
                    '&:hover': {
                      bgcolor: 'action.selected',
                    }
                  }}
                >
                  <Box sx={{ mr: 1, color: getNotificationColor(notification.event) }}>
                    {getNotificationIcon(notification.event)}
                  </Box>
                  <ListItemText
                    primary={notification.message}
                    secondary={
                      <Box>
                        {notification.status === 'unread' && (
                          <Typography variant="caption" color="primary">
                            New
                          </Typography>
                        )}
                      </Box>
                    }
                    sx={{ width: { xs: '100px', sm: '100%' }}}
                  />
                  {notification.video_id && (
                    <IconButton size="small" onClick={() => handleVideoClick(notification.video_id!)}>
                      <PlayArrow />
                    </IconButton>
                  )}
                  <IconButton 
                    size="small" 
                    onClick={(e) => handleClearNotification(e, notification.id)}
                  >
                    <Clear />
                  </IconButton>
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
